import React from 'react';
import { Pagination } from 'react-bootstrap';
import './PaginationControls.css';

/**
 * Reusable pagination controls component
 * 
 * @param {Object} props
 * @param {number} props.currentPage - Current page number (1-based)
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {number} props.maxDisplayed - Maximum number of page buttons to display
 */
const PaginationControls = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  maxDisplayed = 5 
}) => {
  // Don't render pagination if there's only 1 page or no pages
  if (!totalPages || totalPages <= 1) {
    return null;
  }

  // Calculate range of pages to display
  const getPageRange = () => {
    if (totalPages <= maxDisplayed) {
      // If we have fewer pages than max display, show all pages
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Calculate how many pages to show before and after current page
    const sidePages = Math.floor((maxDisplayed - 1) / 2);
    let startPage = Math.max(1, currentPage - sidePages);
    let endPage = startPage + maxDisplayed - 1;

    // Adjust if we're near the end
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxDisplayed + 1);
    }

    return Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);
  };

  const pageRange = getPageRange();

  return (
    <div className="pagination-container">
      <Pagination>
        {/* First page button */}
        <Pagination.First 
          onClick={() => onPageChange(1)} 
          disabled={currentPage === 1} 
        />
        
        {/* Previous page button */}
        <Pagination.Prev 
          onClick={() => onPageChange(currentPage - 1)} 
          disabled={currentPage === 1} 
        />
        
        {/* Show pages */}
        {pageRange.map(page => (
          <Pagination.Item 
            key={page} 
            active={page === currentPage}
            onClick={() => onPageChange(page)}
          >
            {page}
          </Pagination.Item>
        ))}
        
        {/* Next page button */}
        <Pagination.Next 
          onClick={() => onPageChange(currentPage + 1)} 
          disabled={currentPage === totalPages} 
        />
        
        {/* Last page button */}
        <Pagination.Last 
          onClick={() => onPageChange(totalPages)} 
          disabled={currentPage === totalPages} 
        />
      </Pagination>
      
      {/* Page size selector */}
      <div className="page-size-selector">
        <span>Rows per page: </span>
        <select 
          value={localStorage.getItem('preferredPageSize') || '25'}
          onChange={e => {
            localStorage.setItem('preferredPageSize', e.target.value);
            window.location.reload(); // Reload to apply new page size
          }}
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
    </div>
  );
};

export default PaginationControls;
